import React from 'react';
import SwipeSlider from '@wix/da-shared-react/pkg/SwipeSlider.v2';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { MeasuredSlider as Slider } from '@wix/da-shared-react/pkg/Slider';
import {
  PANEL_HEIGHT,
  DEFAULT_THUMB_HEIGHT,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import { BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import { ShopItemType } from '../../../../../../types/shop';
import DuperbrowseContext from '../../../../../contexts/DuperbrowseContext';
import { withMobileContext } from '@wix/da-react-context/pkg/MobileContext';
import withFeatureFlags, {
  WithFeatureFlagsProps,
} from '@wix/da-react-context/pkg/flags/withFeatureFlags';
import {
  withVerification,
  WithVerificationProps,
} from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-bi/pkg/hocs/withLogBiEvent';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import AddItemCard from '../../_partials/AddItemCard';
import WidgetBox, {
  Props as WidgetBoxProps,
} from '../../../../UserWidgets/_partials/WidgetBox';
import CommissionThumbWithMenu from '../../_partials/CommissionThumbWithMenu';
import EmptyState from '../../_partials/EmptyState';
import DisabledOverlay from '../../_partials/DisabledOverlay';
import DisabledEmptyState from '../../_partials/DisabledEmptyState';
import CommissionSliderMobile from './CommissionSliderMobile';

import s from './CommissionSlider.scss';

export interface Props {
  streamId: string;
  moduleId: number;
  commissions: PapiDeviation[];
  total: number;
  isOpenForCommissions: boolean;
  isOwner: boolean;
  username: string;
  isMobile?: boolean;
  openCommissionEditModal: (params?: any) => void;
}

export class CommissionSlider extends React.Component<
  Props &
    WidgetBoxProps &
    WithTranslation &
    WithVerificationProps &
    WithLogBiEventProps &
    WithFeatureFlagsProps
> {
  render() {
    const {
      isMobile,
      t,
      streamId,
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      moduleId,
      commissions,
      total,
      isOpenForCommissions,
      isOwner,
      username,
      section,
      zone,
      flags,
    } = this.props;
    const shop_card = flags.includes('shop_card');

    const isEmpty = !commissions.length;
    const isClosedForCommissions = !isOpenForCommissions;
    // is closed for commissions - visitor
    // is closed for commissions - mobile
    const showDisabledState =
      (!isOwner && isClosedForCommissions) ||
      (isMobile && isClosedForCommissions);

    const shouldRenderEmptyView = isEmpty || showDisabledState;

    // dont show empty state - https://phrac.deviantart.net/T57297 & https://phrac.deviantart.net/T57418
    if (!isOwner && (isEmpty || isClosedForCommissions)) {
      return null;
    }

    return (
      <WidgetBox
        section={section}
        zone={zone}
        header={t!('common.commissions_other')}
        headerClassName={this.props.headerClassName}
        count={total}
        moduleId={moduleId}
        widgetMoveUp={widgetMoveUp}
        widgetMoveDown={widgetMoveDown}
        widgetRemove={widgetRemove}
        isEmpty={shouldRenderEmptyView}
        hasMore={!isEmpty} // until we implement inner slider overflow recognition
        emptyView={
          showDisabledState
            ? this.renderDisabledEmptyView()
            : this.renderEmptyView()
        }
        seeAllUrl={Url.userShopPath(
          username,
          USER_PROFILE_SHOP_SUB_PATHS.COMMISSIONS
        )}
      >
        <DuperbrowseContext
          streamId={streamId}
          deviations={commissions}
          parent={{ title: t('duperbrowse.backButton.profile.shop') }}
        >
          {isMobile ? (
            shop_card ? (
              <CommissionSliderMobile
                deviations={commissions}
                showAddItemCard={isOwner}
                onCreate={this.openCommissionEditModalFromEmptyState}
              />
            ) : (
              <SwipeSlider startSlide={isOwner ? 1 : 0}>
                {isOwner && (
                  <AddItemCard
                    title={t('pages.shop.commissions.addItem.title')}
                    subtitle={t('pages.shop.commissions.addItem.subtitle')}
                    onClick={this.openCommissionEditModalFromHeaderButton}
                    height={PANEL_HEIGHT + DEFAULT_THUMB_HEIGHT}
                    view={'small'}
                    biData={BiData<DefaultBiEvent>({
                      evid: 2,
                      click_info: 'create_commission',
                      sectionname: 'commission',
                    })}
                  />
                )}
                {commissions.map(commission => (
                  <CommissionThumbWithMenu
                    key={commission.deviationId}
                    deviation={commission}
                  />
                ))}
              </SwipeSlider>
            )
          ) : (
            <div className={s['content']}>
              {isClosedForCommissions && <DisabledOverlay />}
              <Slider
                className={s['slider']}
                renderSliderItems={elementSize => (
                  <>
                    {isOwner && (
                      <AddItemCard
                        title={t('pages.shop.commissions.addItem.title')}
                        subtitle={t('pages.shop.commissions.addItem.subtitle')}
                        onClick={this.openCommissionEditModalFromHeaderButton}
                        view={'small'}
                        biData={BiData<DefaultBiEvent>({
                          evid: 2,
                          click_info: 'create_commission',
                          sectionname: 'commission',
                        })}
                      />
                    )}
                    {commissions.map(commission => (
                      <CommissionThumbWithMenu
                        key={commission.deviationId}
                        deviation={commission}
                        width={elementSize.width}
                        height={elementSize.height + PANEL_HEIGHT}
                      />
                    ))}
                  </>
                )}
              />
            </div>
          )}
        </DuperbrowseContext>
      </WidgetBox>
    );
  }

  // - disabled state for visitor
  // - disabled mobile state
  renderDisabledEmptyView = () => <DisabledEmptyState view="wide" />;

  // - empty commissions
  renderEmptyView() {
    const { t, isOwner } = this.props;
    return (
      <EmptyState
        view="wide"
        shopItemType={ShopItemType.COMMISSIONS}
        buttonLabel={
          isOwner ? t('pages.shop.emptyState.commissions.createCTA') : undefined
        }
        onButtonClick={
          isOwner ? this.openCommissionEditModalFromEmptyState : undefined
        }
        biData={BiData<DefaultBiEvent>({
          evid: 2,
          click_info: 'create_commission',
          sectionname: 'commission',
        })}
      />
    );
  }

  openCommissionEditModal = (widgetName: string) => {
    const {
      isVerificationNeeded,
      pushVerificationPopup,
      openCommissionEditModal,
      logBiEvent,
    } = this.props;
    if (isVerificationNeeded) {
      pushVerificationPopup('submitcommission');
      return;
    }

    openCommissionEditModal();
    logBiEvent({
      evid: BiEventType.ADD_NEW_CONTENT_CLICK,
      widgetname: widgetName,
    });
  };

  openCommissionEditModalFromHeaderButton = () =>
    this.openCommissionEditModal('commission_headerbutton');
  openCommissionEditModalFromEmptyState = () =>
    this.openCommissionEditModal('commission_emptystate');
}

export default withVerification(
  withFeatureFlags(
    withMobileContext(withLogBiEvent(withTranslation()(CommissionSlider)))
  )
) as React.ComponentType<Props>;
